import { useState } from 'react'
import { useMutation } from '~/shared/hooks/promiseState'
import { format, isValid } from 'date-fns'
import { ISO_DATE_FORMAT, zonedDateTime } from '~/shared/lib/date'
import {
  carePlanTemplates as carePlanTemplatesApi,
  carePlanTemplatesPreview,
  CarePlanTemplatesListResponse,
  CarePlanTemplatesPreviewRetrieveResponse,
  CarePlanV2ListResponse,
} from '@fireflyhealth/api'
import type { PatientUser } from '~/shared/types'
import type { RootState } from '~/app/store/configureStore'

/**
 * Type alias for a single care plan template from the generated API.
 * Uses snake_case properties (type_id, type_description, episodic, etc.)
 */
export type CarePlanTemplateItem = CarePlanTemplatesListResponse[number]

/**
 * Editable task type for CarePlansModal using snake_case to match API.
 */
export interface CarePlanModalTask {
  id?: number
  title: string
  due_date: string
  owner_group?: number
  priority: number
  is_complete: boolean
  task_template_pk?: number
}

const getDefaultTasks = (providerUser: RootState['me']): CarePlanModalTask[] => [
  {
    title: '',
    due_date: format(new Date(), ISO_DATE_FORMAT),
    owner_group: providerUser?.assignee_group?.id,
    priority: 0,
    is_complete: false,
  },
]

export const CREATE_MODES = {
  TEMPLATE: 'TEMPLATE',
  CUSTOM: 'CUSTOM',
}

/**
 * Maps task preview data from @fireflyhealth/api to editable CarePlanModalTask format.
 */
/** The clinic-zone calendar date of a stored instant, or '' if there isn't one. */
const formatEasternDay = (value: string | null | undefined) => {
  if (!value) return ''
  const day = zonedDateTime(value)
  return isValid(day) ? format(day, ISO_DATE_FORMAT) : ''
}

export const mapCarePlanPreviewTasksToTasks = (
  taskPreviews: CarePlanTemplatesPreviewRetrieveResponse['task_previews'],
  options: { defaultAssigneeGroup?: number },
): CarePlanModalTask[] => {
  return taskPreviews.map(taskPreview => {
    return {
      title: taskPreview.title,
      due_date: formatEasternDay(taskPreview.due_date),
      owner_group: taskPreview.assignee_pk ?? options.defaultAssigneeGroup,
      is_complete: false,
      priority: taskPreview.priority || 0,
      task_template_pk: taskPreview.task_template_pk,
    }
  })
}

const fetchCarePlanTemplates = async () => {
  const { data } = await carePlanTemplatesApi.list()
  return data
}

export const useCarePlanModal = (providerUser: RootState['me']) => {
  const [title, setTitle] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [tasks, setTasks] = useState<CarePlanModalTask[]>(getDefaultTasks(providerUser))
  const [saving, setSaving] = useState<boolean>(false)
  const [createMode, setCreateMode] = useState<string>(CREATE_MODES.CUSTOM)
  const [readyToSaveFromTemplateId, setReadyToSaveFromTemplateId] = useState<number | null>(null)
  const [allTemplateTasksComplete, setAllTemplateTasksComplete] = useState<boolean>(false)
  const [selectedTemplate, setSelectedTemplate] = useState<CarePlanTemplateItem | null>(null)
  // Internal use only checkbox state (visible_to_patient = !internalOnly)
  const [internalOnly, setInternalOnly] = useState<boolean>(false)

  const resetToDefaultState = () => {
    setTitle('')
    setNotes('')
    setTasks(getDefaultTasks(providerUser))
    setSaving(false)
    setCreateMode(CREATE_MODES.CUSTOM)
    setInternalOnly(false)
    resetTemplateWorkflow()
  }

  type CarePlanItem = CarePlanV2ListResponse[number]
  type CarePlanTask = NonNullable<CarePlanItem['tasks']>[number]

  const loadCarePlan = (carePlan: CarePlanItem) => {
    const { title: newTitle, notes: newNotes, visible_to_patient } = carePlan
    const newTasks = carePlan.tasks?.length
      ? carePlan.tasks.map((task: CarePlanTask): CarePlanModalTask => ({
          owner_group: task.owner_group ?? undefined,
          id: task.id,
          title: task.title ?? '',
          // Read in the clinic's zone: setDueDateToEOD stores 7PM Eastern, which
          // is the next UTC day for part of the year.
          due_date: formatEasternDay(task.due_date),
          is_complete: task.is_complete ?? false,
          priority: task.priority ?? 0,
        }))
      : null

    setTitle(newTitle)
    setNotes(newNotes ?? '')
    // Internal only = NOT visible to patient
    setInternalOnly(visible_to_patient === false)
    if (newTasks) {
      setTasks(newTasks)
    }
  }

  const resetTemplateWorkflow = () => {
    setSelectedTemplate(null)
    setReadyToSaveFromTemplateId(null)
  }

  const {
    run: loadCarePlanTemplates,
    loading: carePlanTemplatesLoading,
    result: carePlanTemplates,
  } = useMutation(fetchCarePlanTemplates, {
    initialLoading: true,
  })

  const {
    handler: loadPreviewData,
    loading: previewDataLoading,
    result: previewData,
    error: previewDataError,
  } = useMutation(
    async (selectedTemplate: CarePlanTemplateItem, patient: PatientUser) => {
      const { data } = await carePlanTemplatesPreview.retrieve(
        { id: selectedTemplate.id },
        { params: { user_id: patient.id } },
      )
      setTitle(data.title)
      setNotes(data.notes)
      setReadyToSaveFromTemplateId(selectedTemplate.id)
      return data
    },
    {
      initialLoading: true,
    },
  )

  const selectTemplate = (carePlanTemplateId: number | null, patient: PatientUser) => {
    if (!carePlanTemplateId) {
      resetTemplateWorkflow()
      return
    }
    const carePlanTemplate = (carePlanTemplates ?? []).find(el => el.id === carePlanTemplateId)
    if (!carePlanTemplate) {
      resetTemplateWorkflow()
      return
    }
    if (carePlanTemplateId !== selectedTemplate?.id) {
      setReadyToSaveFromTemplateId(null)
    }
    setSelectedTemplate(carePlanTemplate)
    void loadPreviewData(carePlanTemplate, patient)
  }

  return {
    title,
    setTitle,
    notes,
    setNotes,
    tasks,
    setTasks,
    createMode,
    setCreateMode,
    readyToSaveFromTemplateId,
    setReadyToSaveFromTemplateId,
    allTemplateTasksComplete,
    setAllTemplateTasksComplete,
    selectedTemplate,
    setSelectedTemplate,
    saving,
    setSaving,
    internalOnly,
    setInternalOnly,
    resetToDefaultState,
    loadCarePlan,
    loadCarePlanTemplates,
    carePlanTemplates,
    carePlanTemplatesLoading,
    loadPreviewData,
    previewDataLoading,
    previewData,
    previewDataError,
    resetTemplateWorkflow,
    selectTemplate,
  }
}
